<script setup>
import FlowThreadNode from './FlowThreadNode.vue';
import NodeBadge from './ui/NodeBadge.vue';

defineProps({
  nodeType: {
    type: String,
    required: true,
  },
  continuationItem: {
    type: Object,
    default: null,
  },
  visibleStructuredBranches: {
    type: Array,
    default: () => [],
  },
  interactiveButtonContinuationItems: {
    type: Array,
    default: () => [],
  },
  quickInsertItems: {
    type: Array,
    default: () => [],
  },
  selectedNodeId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'select',
  'insertAfter',
  'updateNode',
  'deleteNode',
  'duplicateNode',
  'addInteractiveButton',
  'moveInteractiveButton',
  'addInteractiveListRow',
  'addInteractiveListSection',
  'quickInsert',
]);

const onSelect = payload => emit('select', payload);
const onInsertAfter = payload => emit('insertAfter', payload);
const onUpdateNode = payload => emit('updateNode', payload);
const onDeleteNode = payload => emit('deleteNode', payload);
const onDuplicateNode = payload => emit('duplicateNode', payload);
const onAddInteractiveButton = payload => emit('addInteractiveButton', payload);
const onMoveInteractiveButton = payload =>
  emit('moveInteractiveButton', payload);
const onAddInteractiveListRow = payload =>
  emit('addInteractiveListRow', payload);
const onAddInteractiveListSection = payload =>
  emit('addInteractiveListSection', payload);
const onQuickInsert = payload => emit('quickInsert', payload);
</script>

<template>
  <div
    v-if="visibleStructuredBranches.length || continuationItem"
    class="relative flex h-5 items-stretch justify-center"
  >
    <div class="w-px rounded-full bg-black/35" />
  </div>

  <div
    v-if="
      nodeType === 'interactive' && interactiveButtonContinuationItems.length
    "
    class="flex w-full flex-col gap-2"
  >
    <div class="flex flex-col gap-2">
      <div
        v-for="branch in interactiveButtonContinuationItems"
        :key="`interactive_branch_${branch.id}`"
        class="flex flex-col gap-1.5"
      >
        <div class="flex items-center gap-2 px-2">
          <NodeBadge :label="branch.label" variant="successOutline" size="sm" />
          <div class="h-px flex-1 bg-black/10" />
        </div>

        <FlowThreadNode
          :item="branch.item"
          :quick-insert-items="quickInsertItems"
          :selected-node-id="selectedNodeId"
          @select="onSelect"
          @insert-after="onInsertAfter"
          @update-node="onUpdateNode"
          @delete-node="onDeleteNode"
          @duplicate-node="onDuplicateNode"
          @add-interactive-button="onAddInteractiveButton"
          @move-interactive-button="onMoveInteractiveButton"
          @add-interactive-list-row="onAddInteractiveListRow"
          @add-interactive-list-section="onAddInteractiveListSection"
          @quick-insert="onQuickInsert"
        />
      </div>
    </div>
  </div>

  <div
    v-if="
      visibleStructuredBranches.length &&
      !(nodeType === 'interactive' && interactiveButtonContinuationItems.length)
    "
    class="flex w-full flex-col gap-2 rounded-[1rem] border border-black/5 bg-white/65 p-2 backdrop-blur-sm"
  >
    <div class="flex flex-wrap gap-2">
      <NodeBadge
        v-for="branch in visibleStructuredBranches"
        :key="`chip_${branch.id}`"
        :label="
          branch.item?.node?.config?.title ||
          branch.item?.node?.config?.buttonText ||
          branch.item?.node?.label ||
          branch.output
        "
        variant="whiteSolid"
        size="md"
      />
    </div>

    <div class="flex flex-col gap-2.5">
      <FlowThreadNode
        v-for="branch in visibleStructuredBranches"
        :key="branch.id"
        :item="branch.item"
        :quick-insert-items="quickInsertItems"
        :selected-node-id="selectedNodeId"
        @select="onSelect"
        @insert-after="onInsertAfter"
        @update-node="onUpdateNode"
        @delete-node="onDeleteNode"
        @duplicate-node="onDuplicateNode"
        @add-interactive-button="onAddInteractiveButton"
        @move-interactive-button="onMoveInteractiveButton"
        @add-interactive-list-row="onAddInteractiveListRow"
        @add-interactive-list-section="onAddInteractiveListSection"
        @quick-insert="onQuickInsert"
      />
    </div>
  </div>

  <FlowThreadNode
    v-if="continuationItem"
    :item="continuationItem"
    :quick-insert-items="quickInsertItems"
    :selected-node-id="selectedNodeId"
    @select="onSelect"
    @insert-after="onInsertAfter"
    @update-node="onUpdateNode"
    @delete-node="onDeleteNode"
    @duplicate-node="onDuplicateNode"
    @add-interactive-button="onAddInteractiveButton"
    @move-interactive-button="onMoveInteractiveButton"
    @add-interactive-list-row="onAddInteractiveListRow"
    @add-interactive-list-section="onAddInteractiveListSection"
    @quick-insert="onQuickInsert"
  />
</template>
